import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { jobsAPI } from '../utils/api';

/**
 * Public page reached from the post-interview email. The candidate rates the
 * interview (1 to 5) and may add a comment. Token-gated; once per interview.
 * Their answer goes to recruiters only, never to interviewers.
 */
const RATINGS = [
  { value: 1, label: 'Poor' },
  { value: 2, label: 'Fair' },
  { value: 3, label: 'OK' },
  { value: 4, label: 'Good' },
  { value: 5, label: 'Excellent' }
];

const InterviewFeedback = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await jobsAPI.getInterviewFeedbackContext(interviewId, token);
        setContext(res.data.interview);
        if (res.data.interview.submitted) setDone(true);
      } catch (err) {
        setError(err.response?.data?.error || 'This feedback link is not valid.');
      } finally {
        setLoading(false);
      }
    };
    if (interviewId && token) load(); else { setError('This feedback link is not valid.'); setLoading(false); }
  }, [interviewId, token]);

  const submit = async () => {
    if (submitting || !rating) return;
    setSubmitting(true);
    setError('');
    try {
      await jobsAPI.submitCandidateInterviewFeedback(interviewId, token, rating, comments.trim());
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to record feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const when = context?.scheduledDate
    ? new Date(context.scheduledDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '';

  const card = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '1.75rem', maxWidth: '560px', margin: '2rem auto' };

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', padding: '1.5rem 2rem', textAlign: 'center' }}>
        <h1 style={{ color: '#fff', margin: 0, fontSize: '1.5rem', fontWeight: 700, cursor: 'pointer' }} onClick={() => navigate('/')}>SecHire</h1>
      </div>

      <div style={{ padding: '0 1rem' }}>
        {loading ? (
          <div style={card}><p style={{ margin: 0, color: '#6B7280' }}>Loading...</p></div>
        ) : done ? (
          <div style={card}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#111827' }}>Thank you</h2>
            <p style={{ margin: 0, color: '#4B5563' }}>Your feedback has been recorded. It goes to the recruitment team only and does not affect your application.</p>
          </div>
        ) : error && !context ? (
          <div style={card}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#111827' }}>This link is not valid</h2>
            <p style={{ margin: 0, color: '#4B5563' }}>{error}</p>
          </div>
        ) : (
          <div style={card}>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.25rem', color: '#111827' }}>How was your interview?</h2>
            <p style={{ margin: '0 0 1.25rem', color: '#6B7280', fontSize: '0.9rem' }}>
              {context.jobTitle}{when ? ` · ${when}` : ''}. Two minutes. Seen by the recruitment team only, not by your interviewer, and it does not affect your application.
            </p>

            <div role="radiogroup" aria-label="Rating" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              {RATINGS.map(r => (
                <button
                  key={r.value}
                  type="button"
                  role="radio"
                  aria-checked={rating === r.value}
                  onClick={() => setRating(r.value)}
                  style={{
                    flex: '1 1 80px', padding: '0.6rem 0.25rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                    border: rating === r.value ? '2px solid #1E40AF' : '1px solid #D1D5DB',
                    background: rating === r.value ? '#EFF6FF' : '#fff', color: rating === r.value ? '#1E40AF' : '#374151'
                  }}
                >
                  <div style={{ fontSize: '1.1rem' }}>{r.value}</div>
                  <div style={{ fontWeight: 500, fontSize: '0.75rem' }}>{r.label}</div>
                </button>
              ))}
            </div>

            <label htmlFor="comments" style={{ display: 'block', fontSize: '0.85rem', color: '#374151', fontWeight: 500, marginBottom: '0.4rem' }}>
              Anything you would like the team to know? (optional)
            </label>
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value.slice(0, 2000))}
              rows={4}
              placeholder="What went well, what could be better"
              style={{ width: '100%', padding: '0.625rem', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: '1rem' }}
            />

            {error && <p style={{ color: '#B91C1C', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>{error}</p>}

            <button
              type="button"
              onClick={submit}
              disabled={!rating || submitting}
              style={{ padding: '0.6rem 1.4rem', background: !rating || submitting ? '#D1D5DB' : '#1E40AF', color: '#fff', border: 'none', borderRadius: '6px', cursor: !rating || submitting ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: 600 }}
            >
              {submitting ? 'Sending...' : 'Send feedback'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewFeedback;
